import React, { useRef, useState } from "react";
import api from "@/api";
import { useApp } from "@/context/AppContext";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Voice recorder button. On click: start recording (MediaRecorder → webm).
 * On stop: uploads to /api/voice/parse and calls onLines(lines, transcript).
 * Props: onLines(lines, transcript), size ("md"|"lg"), disabled
 */
export default function VoiceRecorder({ onLines, size = "md", disabled = false }) {
  const { t } = useApp();
  const [state, setState] = useState("idle"); // idle | recording | processing
  const [transcript, setTranscript] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    setTranscript("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        streamRef.current?.getTracks().forEach((tr) => tr.stop());
        setState("processing");
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await api.post("/voice/parse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          setTranscript(r.data.transcript || "");
          if (!r.data.lines || r.data.lines.length === 0) {
            toast.warning(t("voice_no_lines"));
          } else {
            onLines(r.data.lines, r.data.transcript);
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Voice failed");
        } finally { setState("idle"); }
      };
      rec.start();
      setState("recording");
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch (e) {}
  };

  const cls = size === "lg" ? "h-14 px-6 text-base" : "h-11 px-4 text-sm";
  const label =
    state === "recording" ? t("stop_recording")
    : state === "processing" ? t("processing_voice")
    : t("voice_add");

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={state === "recording" ? stop : start}
        disabled={disabled || state === "processing"}
        data-testid="voice-btn"
        data-state={state}
        className={`${cls} rounded-xl font-semibold flex items-center gap-2 transition-colors ${
          state === "recording" ? "bg-red-600 hover:bg-red-700 text-white animate-pulse" :
          state === "processing" ? "bg-stone-200 text-stone-600" :
          "bg-stone-100 hover:bg-stone-200 text-stone-800"
        }`}
      >
        {state === "processing" ? <Loader2 className="w-4 h-4 animate-spin" /> :
         state === "recording" ? <Square className="w-4 h-4" /> :
         <Mic className="w-4 h-4" />}
        {label}
      </button>
      {transcript && (
        <div className="text-[11px] text-stone-500 max-w-xs truncate" data-testid="voice-transcript">
          {t("voice_transcript")}: <span className="italic">{transcript}</span>
        </div>
      )}
    </div>
  );
}
